import { motion } from 'framer-motion';
import { X, Instagram, Linkedin, Phone, MapPin, ArrowUp } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';
import { Link } from '../router/Link';
import { GoogleMap as GlobalMap } from './GlobalMap';

export function Footer() {
    const currentYear = new Date().getFullYear();
    const { t } = useLanguage();
    const { config, getAsset } = useWebsite();

    // Get website-specific footer styles
    const getFooterStyles = () => {
        const websiteName = config.name.toLowerCase();

        switch (websiteName) {
            case 'bluesky':
                return {
                    // Modern, clean design with rounded elements
                    socialButtonClass: 'rounded-full',
                    newsletterButtonClass: 'rounded-full',
                    linkHoverEffect: 'hover:text-white hover:scale-105',
                    buttonTransition: 'transition-all duration-300 ease-out'
                };
            case 'emcor':
                return {
                    // Industrial, bold design with moderate rounded elements
                    socialButtonClass: 'rounded-lg',
                    newsletterButtonClass: 'rounded-lg',
                    linkHoverEffect: 'hover:text-white hover:border-red-500/50',
                    buttonTransition: 'transition-all duration-250 ease-in-out'
                };
            case 'basrasky':
                return {
                    // Eco-friendly, organic design with nature-inspired shapes
                    socialButtonClass: 'rounded-2xl',
                    newsletterButtonClass: 'rounded-2xl',
                    linkHoverEffect: 'hover:text-white hover:scale-110',
                    buttonTransition: 'transition-all duration-400 ease-in-out'
                };
            default:
                return {
                    socialButtonClass: 'rounded-lg',
                    newsletterButtonClass: 'rounded-lg',
                    linkHoverEffect: 'hover:text-white',
                    buttonTransition: 'transition-all duration-300'
                };
        }
    };

    const footerStyles = getFooterStyles();

    const socialLinks = [
        { icon: X, href: "#", label: "X (Twitter)" },
        { icon: Instagram, href: "#", label: "Instagram" },
        { icon: Linkedin, href: "#", label: "LinkedIn" },
    ];

    const footerSections = [
        {
            title: t("footer.company.title"),
            links: [
                { name: t("footer.company.link1"), href: "/about" },
                { name: t("footer.company.link2"), href: "/services" },
                { name: t("footer.company.link3"), href: "/careers" },
                { name: t("footer.company.link4"), href: "/news" }
            ]
        },
        {
            title: t("footer.solutions.title"),
            links: [
                { name: t("footer.solutions.link1"), href: "/services" },
                { name: t("footer.solutions.link2"), href: "/services" },
                { name: t("footer.solutions.link3"), href: "/services" },
                { name: t("footer.solutions.link4"), href: "/services" }
            ]
        },
        {
            title: t("footer.industries.title"),
            links: [
                { name: t("footer.industries.link1"), href: "/services" },
                { name: t("footer.industries.link2"), href: "/services" },
                { name: t("footer.industries.link3"), href: "/services" },
                { name: t("footer.industries.link4"), href: "/services" },
                { name: t("footer.industries.link5"), href: "/services" }
            ]
        }
    ];

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <footer className="bg-primary text-white overflow-x-hidden" >
            {/* Container applied only on lg and up */}
            <div className="lg:container lg:mx-auto lg:px-6">
                <div className="px-6 py-16 max-w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                        {/* Brand Section */}
                        <div className="lg:col-span-1 min-w-0">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                                className="text-center md:text-left"
                            >
                                <div className="flex items-center justify-center md:justify-start mb-4">
                                    <img
                                        src={getAsset(config.assets.logo)}
                                        alt={`${config.name} Logo`}
                                        className="w-14 h-14 object-contain mr-3"
                                        style={{
                                            width: config.name === 'Bluesky' ? '50px' : config.name === 'Basrasky' ? '50px' : '100px',
                                            height: config.name === 'Bluesky' ? '50px' : config.name === 'Basrasky' ? '50px' : '100px',
                                            objectFit: 'contain',
                                            transition: 'all 300ms',
                                            marginTop: '8px',
                                            marginBottom: '8px',
                                            filter: config.name === 'Basrasky'
                                                ? 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(2000%) hue-rotate(120deg) brightness(1) contrast(1.2)' : ""

                                        }}
                                    />

                                </div>
                                <p className="text-white/80 leading-relaxed mb-6 max-w-md mx-auto md:mx-0">
                                    {t("footer.company.name.description")}
                                </p>
                                <div className="flex justify-center md:justify-start gap-3">
                                    {socialLinks.map((social, index) => (
                                        <motion.a
                                            key={index}
                                            href={social.href}
                                            aria-label={social.label}
                                            whileHover={{ scale: 1.1, y: -2 }}
                                            whileTap={{ scale: 0.95 }}
                                            className={`w-9 h-9 bg-white/10 ${footerStyles.socialButtonClass} flex items-center justify-center hover:bg-white/20 ${footerStyles.buttonTransition}`}
                                        >
                                            <social.icon size={16} />
                                        </motion.a>
                                    ))}
                                </div>
                            </motion.div>
                        </div>

                        {/* Links Sections */}
                        {footerSections.map((section, index) => (
                            <motion.div
                                key={section.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="min-w-0 text-center md:text-left"
                            >
                                <h4 className="font-semibold mb-4 text-lg">{section.title}</h4>
                                <ul className="space-y-2">
                                    {section.links.map((link, linkIndex) => (
                                        <li key={linkIndex}>
                                            <Link
                                                to={link.href}
                                                className={`text-white/70 ${footerStyles.linkHoverEffect} ${footerStyles.buttonTransition} text-sm inline-block`}
                                            >
                                                {link.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Global Map Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        viewport={{ once: true }}
                        className="pt-8 mb-8"
                    >
                        {/* Map Component */}
                        <div className="mb-6">
                            <GlobalMap />
                        </div>

                        <div className="text-center px-4 mb-6">
                            <h4 className="text-xl font-semibold text-white mb-1">{t("footer.global.title")}</h4>
                            <div className="text-sm text-white/80 mb-4">{t("footer.global.subtitle")}</div>
                        </div>

                        {/* Contact Information */}
                        <div className="text-center px-4">
                            <div className="mt-6 space-y-2">
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-white/80">
                                    <div className="flex items-center">
                                        <Phone className="w-4 h-4 mr-2" />
                                        <span className="font-medium mr-2">UAE:</span>
                                        <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                                            {t("footer.contact.phone.uae")}
                                        </span>
                                    </div>
                                    {config.name === "EMCOR" && (
                                        <div className="flex items-center">
                                            <Phone className="w-4 h-4 mr-2" />
                                            <span className="font-medium mr-2">Switzerland:</span>
                                            <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                                                {t("footer.contact.phone.switzerland")}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-center text-sm text-white/80">
                                    <MapPin className="w-4 h-4 mr-2" />
                                    <span>{t("footer.contact.address")}</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Newsletter Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        viewport={{ once: true }}
                        className="border-t border-white/20 pt-8 mb-16"
                    >
                        <div className="flex flex-col md:flex-row items-center justify-between flex-wrap gap-4">
                            <div className="mb-4 md:mb-0 max-w-full md:max-w-xs text-center md:text-left">
                                <h4 className="font-semibold mb-2">{t("footer.newsletter.title")}</h4>
                                <p className="text-white/70 text-sm">{t("footer.newsletter.description")}</p>
                            </div>
                            <div className="flex w-full max-w-md flex-wrap gap-3 justify-center md:justify-start items-center">
                                <input
                                    type="email"
                                    placeholder={t("footer.newsletter.placeholder")}
                                    className={`flex-grow min-w-0 px-4 py-3 bg-white/10 border border-white/20 ${footerStyles.newsletterButtonClass} focus:outline-none focus:border-white/40 ${footerStyles.buttonTransition} text-sm`}
                                />

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`bg-white hover:bg-white/90 text-primary hover:text-primary border border-primary/30 ${footerStyles.newsletterButtonClass} px-8 py-3 ${footerStyles.buttonTransition} flex-shrink-0 shadow-sm hover:shadow-md font-medium`}
                                >
                                    {t("footer.newsletter.subscribe")}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        viewport={{ once: true }}
                        className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between flex-wrap gap-4 text-center md:text-left"
                    >
                        <div className="flex flex-col md:flex-row md:items-center text-white/70 text-sm gap-2 md:gap-0 justify-center md:justify-start w-full md:w-auto">
                            <span>© {currentYear} - {t("footer.company.name.title")}</span>
                            <span className="hidden md:inline mx-2">•</span>
                            <span>{t("footer.privacy")}</span>
                            <span className="hidden md:inline mx-2">•</span>
                            <span>{t("footer.terms_of_use")}</span>
                            <span className="mt-2 md:mt-0 md:ml-4 text-xs text-white/50 whitespace-nowrap">
                                Website by{" "}
                                <a
                                    href="https://palmcode.ae"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-white/80"
                                >
                                    Palm Code
                                </a>
                            </span>
                        </div>

                        <motion.button
                            onClick={scrollToTop}
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            className={`text-white/70 hover:text-white ${footerStyles.buttonTransition} flex items-center text-sm justify-center md:justify-start`}
                        >
                            {t("footer.back_to_top")}
                            <ArrowUp className="ml-1 w-4 h-4" />
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        </footer>
    );
}
